import React, { useRef, useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  IconButton,
  Flex,
  useBreakpointValue,
  Avatar,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";

const cardWidth = 400;
const gap = 32; // 8 * 4 = 32px
const cardWithGap = cardWidth + gap;

const testimonials = [
  {
    text: "Nathan was a pleasure to work with and delivered a beautiful website  along with all design components. He’s time efficient, technically skilled, has incredible eye for detail, and is always up for a new challenge.",
    author: "Taylor Lee",
    position: "CEO",
    company: "Wander Collective",
    image: "/assets/images/testimonials/taylor-lee.jpeg",
  },
  {
    text: "I worked with Nathan between 2019 and 2020, when he was working as a Web Designer. During this period, he was responsible for creating graphic materials, developing WordPress websites, landing pages, and marketing emails, as well as producing and editing videos and photos, and managing social media. In all activities, he demonstrated technical excellence, attention to detail, and a strong commitment to delivering high-quality results. In addition to his technical talent, he was proactive, dedicated, and always brought innovative ideas and creative suggestions for improvements.",
    author: "Grazzielle Fredericci",
    position: "Marketing Manager",
    company: "SP Intervention",
    image: "/assets/images/testimonials/grazzielle.jpeg",
  },
];

const TestimonialCard = ({ testimonial, isCenter }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{ duration: 0.5 }}
      style={{
        flexShrink: 0,
        width: `${cardWidth}px`,
        textAlign: "center",
        transform: isCenter ? "scale(1.05)" : "scale(1)",
        transition: "transform 0.5s ease, all 0.3s ease",
        zIndex: isCenter ? 10 : 1,
      }}
    >
      <Box
        bg="#333333"
        p={6}
        rounded="3xl"
        backdropFilter="blur(10px)"
        border={`2px ${isCenter ? " dashed rgba(0, 0, 0, 0.7)" : "dashed rgba(0, 0, 0, 0.2)"}`}
        h="full"
        boxShadow={isCenter ? "0 20px 40px rgba(0, 0, 0, 0.3)" : "0 10px 20px rgba(0, 0, 0, 0.2)"}
        opacity={isCenter ? 1 : 0.5}
      >
        <VStack spacing={4} align="center">
          <Flex align="center" gap={4}>
            <Avatar src={testimonial.image} size="xl" />
            <VStack align="start" spacing={0}>
              <Text color="#DD9331" fontWeight="bold">
                {testimonial.author}
              </Text>
              <Text color="white" fontSize="sm" opacity={0.8}>
                {testimonial.position}
              </Text>
              <Text color="white" fontSize="sm" opacity={0.6}>
                {testimonial.company}
              </Text>
            </VStack>
          </Flex>
          <Text
            fontSize="md"
            color="white"
            fontStyle="italic"
            lineHeight="1.2"
            whiteSpace="normal"
            wordBreak="break-word"
          >
            "{testimonial.text}"
          </Text>
        </VStack>
      </Box>
    </motion.div>
  );
};

const Testimonials = () => {
  const scrollContainerRef = useRef(null);
  const perPage = useBreakpointValue({ base: 1, md: 2, lg: 3 }) || 3;
  const containerWidth = perPage * cardWidth + (perPage - 1) * gap;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Create loop by duplicating testimonials for seamless scrolling
  const extendedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  // Calculate the desired left position for centering the active card
  const desiredLeft = containerWidth / 2 - cardWidth / 2;
  const currentOffset = (activeIndex + testimonials.length) * cardWithGap - desiredLeft;

  const nextTestimonial = () => {
    if (isTransitioning) return;
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevTestimonial = () => {
    if (isTransitioning) return;
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  return (
    <Box
      h="100vh"
      minH="100vh"
      className="scroll-section"
      scrollSnapAlign="start"
      id="testimonials"
      background={"#F9F0DF"}
    >
      <Box minH="100%" p="30px" display="flex" flexDirection="column" justifyContent="center">
        <motion.div
          initial={{ opacity: 1, transform: "translateY(40px)" }}
          transition={{ ease: "linear", delay: 0.2 }}
          whileInView={{ opacity: 0, transform: "translateY(-40px)" }}
        >
          <Heading as="h2" size="2xl" mb="10" textAlign="center" color="#333333">
            Trusted by
          </Heading>
        </motion.div>

        {/* Testimonials Carousel - Loop with 3 per page */}
        <Box
          ref={scrollContainerRef}
          overflow="visible"
          whiteSpace="nowrap"
          py={8}
          w={`${containerWidth}px`}
          mx="auto"
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            display="flex"
            gap={8}
            flexWrap="nowrap"
            style={{
              width: "fit-content",
              transform: `translateX(-${currentOffset}px)`,
              transition: "transform 0.5s ease-in-out",
            }}
          >
            {extendedTestimonials.map((testimonial, index) => {
              const realIndex = index % testimonials.length;
              const isCenter = realIndex === activeIndex;
              return (
                <Box key={`${testimonial.author}-${index}`} style={{ flexShrink: 0 }}>
                  <TestimonialCard testimonial={testimonial} isCenter={isCenter} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Indicator dots and controls */}
        <Flex justify="center" align="center" mt={8} gap={4} px={4}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={prevTestimonial}
            disabled={isTransitioning}
            bg="rgba(0, 0, 0, 0.1)"
            color="#333333"
            _hover={{ bg: "rgba(0, 0, 0, 0.2)", transform: "scale(1.1)" }}
            _active={{ bg: "rgba(0, 0, 0, 0.3)" }}
            borderRadius="full"
            size="lg"
            transition="all 0.3s ease"
          />
          <Flex gap={2}>
            {testimonials.map((_, index) => (
              <Box
                key={index}
                w="12px"
                h="12px"
                borderRadius="full"
                bg="rgba(0, 0, 0, 0.3)"
                cursor="pointer"
                onClick={() => {
                  if (isTransitioning) return;
                  setActiveIndex(index);
                  setIsTransitioning(true);
                  setTimeout(() => setIsTransitioning(false), 500);
                }}
                transition="all 0.3s ease"
                _hover={{ bg: "rgba(0, 0, 0, 0.6)" }}
              />
            ))}
          </Flex>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={nextTestimonial}
            disabled={isTransitioning}
            bg="rgba(0, 0, 0, 0.1)"
            color="#333333"
            _hover={{ bg: "rgba(0, 0, 0, 0.2)", transform: "scale(1.1)" }}
            _active={{ bg: "rgba(0, 0, 0, 0.3)" }}
            borderRadius="full"
            size="lg"
            transition="all 0.3s ease"
          />
        </Flex>
      </Box>
    </Box>
  );
};

export default Testimonials;
